import React, { useState } from 'react';
import { SetupState } from '../types';
import { getSafeAdminBaseUrl } from './SuccessPopup';
import { provisionToApartment } from '../lib/apiClient';
import { readSessionToken } from '../lib/appSession';
import { fetchCentralPlanCatalog } from '../services/planCatalogClient';
import { fetchCentralLicenseStatus, LicenseStatus } from '../services/licenseStatusClient';
import { buildApartmentRuntimeConfig, downloadRuntimeConfig } from '../lib/configBuilder';
import { RuntimeConfig } from '../types';
import jsQR from 'jsqr';
import {
  RotateCcw,
  User,
  Building2,
  CreditCard,
  CheckCircle2,
  MapPin,
  DoorClosed,
  Zap,
  Droplet,
  ShieldCheck,
  ChevronRight,
  Sparkles,
  Info,
  Copy,
  Check,
  ExternalLink,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Award,
  Flame,
  MessageSquare,
  Package,
  Palette,
  X,
  AlertCircle
} from 'lucide-react';
import { callMockProvisionEndpoint, MockProvisionResponse } from '../lib/mockProvisionClient';
import { readSessionTenantId } from '../lib/appSession';

interface DormDashboardProps {
  onReset: () => void;
  state: SetupState;
  onChange?: (updates: Partial<SetupState>) => void;
}

const normalizeRateType = (value: unknown) =>
  String(value || '').trim().toLowerCase();

const getWaterUnitLabel = (rateType: unknown) => {
  const type = normalizeRateType(rateType);
  if (type === 'per_unit' || type === 'unit') return 'บาท/หน่วย';
  if (type === 'per_person' || type === 'person' || type === 'person_based') return 'บาท/คน';
  if (type === 'per_room' || type === 'room') return 'บาท/ห้อง';
  if (type === 'flat' || type === 'fixed' || type === 'lump_sum') return 'เหมาจ่าย';
  return 'บาท/ห้อง';
};

const getElectricityUnitLabel = (rateType: unknown) => {
  const type = normalizeRateType(rateType);
  if (type === 'per_unit' || type === 'unit') return 'บาท/หน่วย';
  if (type === 'per_person' || type === 'person' || type === 'person_based') return 'บาท/คน';
  if (type === 'per_room' || type === 'room') return 'เหมาจ่าย';
  if (type === 'flat' || type === 'fixed' || type === 'lump_sum') return 'เหมาจ่าย';
  return 'บาท/หน่วย';
};

const getPersonRoomUnitLabel = (rateType: unknown, fallback = 'บาท/ห้อง') => {
  const type = normalizeRateType(rateType);
  if (type === 'per_person' || type === 'person' || type === 'person_based') return 'บาท/คน';
  if (type === 'per_room' || type === 'room') return 'บาท/ห้อง';
  return fallback;
};

const getSubscriptionPlans = async (token: string): Promise<any> => ({ ok: false, plans: [] });
const createPaymentIntent = async (token: string, tenantId: string, planId: string): Promise<any> => ({ ok: false });
const verifyPaymentSlip = async (token: string, subId: string, file: File, qr: string, amount: number): Promise<any> => ({ ok: false });

export const DormDashboard: React.FC<DormDashboardProps> = ({ onReset, state, onChange }) => {

  const [activeTab, setActiveTab] = useState<'status-preview' | 'profile' | 'line-bot' | 'pricing-plans' | 'integration-payload'>('status-preview');
  const [actionMessage, setActionMessage] = useState('');
  const [lineSubTab, setLineSubTab] = useState<'settings' | 'instructions'>('settings');

  // LINE Bot Credentials State
  const [channelId, setChannelId] = useState('');
  const [channelSecret, setChannelSecret] = useState('');
  const [showChannelSecret, setShowChannelSecret] = useState(false);
  const [isCheckingLine, setIsCheckingLine] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Selected Pricing Plan Status State inside system checkout wizard
  const [selectedRenewalPlanId, setSelectedRenewalPlanId] = useState<string | null>(null);
  const [renewalStep, setRenewalStep] = useState<1 | 2>(1);
  const [isUploadingSlip, setIsUploadingSlip] = useState<boolean>(false);
  const [isRenewalCompleted, setIsRenewalCompleted] = useState<boolean>(false);
  const [copiedPromptPay, setCopiedPromptPay] = useState(false);

  // New states for API integration
  const [apiPlans, setApiPlans] = useState<any[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState<any>(null);
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string>('');
  const [submitError, setSubmitError] = useState('');
  const [uploadedSlipName, setUploadedSlipName] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // States for Integration Payload
  const [integrationPayload, setIntegrationPayload] = useState<RuntimeConfig | null>(null);
  const [isFetchingPayload, setIsFetchingPayload] = useState(false);
  const [payloadError, setPayloadError] = useState('');
  const [copiedPayload, setCopiedPayload] = useState(false);

  // States for Mock Provision Dev Panel
  const [mockProvisionEndpoint, setMockProvisionEndpoint] = useState('');
  const [mockProvisionToken, setMockProvisionToken] = useState('');
  const [mockProvisionAllowDevEndpoint, setMockProvisionAllowDevEndpoint] = useState(false);
  const [mockProvisionLoading, setMockProvisionLoading] = useState(false);
  const [mockProvisionResult, setMockProvisionResult] = useState<MockProvisionResponse | null>(null);
  const [realProvisionLoading, setRealProvisionLoading] = useState(false);
  const [realProvisionResult, setRealProvisionResult] = useState<any | null>(null);
  const [adminTokenLoading, setAdminTokenLoading] = useState(false);
  const [adminTokenUrl, setAdminTokenUrl] = useState<string | null>(null);
  const [fallbackAdminUrl, setFallbackAdminUrl] = useState<string | null>(null);
  const [tenantPreviewLoading, setTenantPreviewLoading] = useState(false);
  const [tenantPreviewUrl, setTenantPreviewUrl] = useState<string | null>(null);
  const [fallbackTenantUrl, setFallbackTenantUrl] = useState<string | null>(null);
  const [isMockProvisionDialogOpen, setIsMockProvisionDialogOpen] = useState(false);

  // License Status Live Switch (Disabled by default)
  const isLiveLicenseEnabled = import.meta.env.VITE_ENABLE_LIVE_LICENSE_STATUS === 'true';
  const [liveLicenseStatus, setLiveLicenseStatus] = useState<LicenseStatus | null>(null);
  const [isFetchingLiveLicense, setIsFetchingLiveLicense] = useState(false);

  const fallbackLicenseStatus: LicenseStatus = {
    status: 'staging_demo',
    planName: 'Demo Plan',
    productionEnabled: false,
    renewalAvailable: false,
    publicSafe: true
  } as unknown as LicenseStatus;
  const licenseStatus = isLiveLicenseEnabled && liveLicenseStatus ? liveLicenseStatus : fallbackLicenseStatus;

  const handleRealProvision = async () => {
    try {
      setRealProvisionLoading(true);
      setRealProvisionResult(null);
      setAdminTokenUrl(null);
      const token = readSessionToken();
      if (!token) throw new Error('No session token — กรุณาล็อกอินใหม่');

      const HORPLUS_WORKER_BASE = import.meta.env.VITE_ADMIN_PROXY_URL
        || 'https://api.horplus.com';

      const dormName = state.dormName || state.dorm_name || '';
      const publicDormId = state.publicDormId || state.public_dorm_id || '';

      const payload: Record<string, any> = {
        idempotencyKey: `wizard-validate-${Date.now()}`,
        dormName,
        publicDormId,
        themeSettings: {
          primaryColor: state.theme?.primaryColor || state.themeSettings?.primaryColor || '#1B5E20',
          logoUrl: state.logoUrl || null,
        },
        billingSettings: {
          electricityRate: state.electricityRate ?? state.billingSettings?.electricityRate,
          waterRate: state.waterRate ?? state.billingSettings?.waterRate,
          waterRateType: state.waterRateType ?? state.billingSettings?.waterRateType ?? 'per_unit',
          commonFee: state.commonFee ?? state.billingSettings?.commonFee ?? 0,
          wifiFee: state.wifiFee ?? state.billingSettings?.wifiFee ?? 0,
          promptPayNumber: state.promptPayNumber ?? state.billingSettings?.promptPayNumber ?? null,
          promptPayName: state.promptPayName ?? state.billingSettings?.promptPayName ?? null,
          phone: state.phone ?? state.billingSettings?.phone ?? null,
        },
      };

      // SAFETY: Never send browser-supplied owner_id as authoritative — Worker derives it from the token.

      const res = await fetch(`${HORPLUS_WORKER_BASE}/admin/proxy/v2/provision/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        setRealProvisionResult({ ok: false, error: data.error || `HTTP ${res.status}`, bucket: data.bucket, wouldWrite: false });
      } else {
        setRealProvisionResult({ ok: true, data, wouldWrite: false });
      }
    } catch (e: any) {
      setRealProvisionResult({ ok: false, error: e.message || 'Validate Failed', wouldWrite: false });
    } finally {
      setRealProvisionLoading(false);
    }
  };


  const handleOpenAdminV2 = async () => {
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    setFallbackAdminUrl(null);
    try {
      setAdminTokenLoading(true);
      const sessionToken = readSessionToken();
      if (!sessionToken) throw new Error('No session token');
      const tenantId = readSessionTenantId();
      if (!tenantId) throw new Error('No tenant ID');

      const { getAdminV2LoginUrl } = await import('../lib/apiClient');
      const res = await getAdminV2LoginUrl(sessionToken, tenantId);
      if (!res?.url) throw new Error('Failed to get admin URL');

      if (popup) {
        popup.location.href = res.url;
      } else {
        setFallbackAdminUrl(window.location.origin + res.url);
      }
    } catch (e: any) {
      if (popup) popup.close();
      alert('ไม่สามารถเปิดหน้าจัดการหอพักได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setAdminTokenLoading(false);
    }
  };

  const handleOpenTenantPreview = async () => {
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    setFallbackTenantUrl(null);
    try {
      setTenantPreviewLoading(true);
      const sessionToken = readSessionToken();
      if (!sessionToken) throw new Error('No session token');
      const tenantId = readSessionTenantId();
      if (!tenantId) throw new Error('No tenant ID');

      const { getTenantV2OwnerPreviewUrl } = await import('../lib/apiClient');
      const res = await getTenantV2OwnerPreviewUrl(sessionToken, tenantId);
      if (!res?.url) throw new Error('Failed to get tenant preview URL');

      if (popup) {
        popup.location.href = res.url;
      } else {
        setFallbackTenantUrl(window.location.origin + res.url);
      }
    } catch (e: any) {
      if (popup) popup.close();
      alert('ไม่สามารถเปิดหน้าผู้เช่าได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setTenantPreviewLoading(false);
    }
  };


  const handleOpenMockProvisionDialog = () => {
    // Generate fresh payload first to ensure it's up to date
    try {
      const payload = buildApartmentRuntimeConfig(state);
      setIntegrationPayload(payload);
      setPayloadError('');
      setIsMockProvisionDialogOpen(true);
    } catch (err: any) {
      setPayloadError(err.message || 'Unknown configuration error');
    }
  };

  const handleCloseMockProvisionDialog = () => {
    setIsMockProvisionDialogOpen(false);
    setMockProvisionToken(''); // Clear sensitive data on close
    setMockProvisionResult(null);
  };

  const handleCallMockProvision = async () => {
    if (!mockProvisionEndpoint || !mockProvisionToken || !integrationPayload) return;

    setMockProvisionLoading(true);
    setMockProvisionResult(null);

    try {
      const result = await callMockProvisionEndpoint({
        endpoint: mockProvisionEndpoint,
        authToken: mockProvisionToken,
        allowProductionEndpoint: mockProvisionAllowDevEndpoint,
        idempotencyKey: 'wizard-mock-' + Date.now()
      }, integrationPayload);

      setMockProvisionResult(result);
    } catch (err: any) {
      setMockProvisionResult({
        ok: false,
        errorCode: 'UNEXPECTED_UI_ERROR',
        warnings: [err.message]
      });
    } finally {
      setMockProvisionLoading(false);
    }
  };

  React.useEffect(() => {
    if (activeTab === 'pricing-plans') {
      loadPlans();
    }
  }, [activeTab]);

  React.useEffect(() => {
    if (isLiveLicenseEnabled) {
      const token = readSessionToken();
      if (token) {
        setIsFetchingLiveLicense(true);
        fetchCentralLicenseStatus(token).then((res: any) => {
          if (res.ok && res.status) {
            setLiveLicenseStatus(res.status);
          } else {
            // Fallback safe status
            setLiveLicenseStatus({ status: 'unknown', productionEnabled: false, publicSafe: true } as unknown as LicenseStatus);
          }
        }).catch(() => {
          setLiveLicenseStatus({ status: 'unknown', productionEnabled: false, publicSafe: true } as unknown as LicenseStatus);
        }).finally(() => {
          setIsFetchingLiveLicense(false);
        });
      }
    }
  }, [isLiveLicenseEnabled]);

  const [apiPlansError, setApiPlansError] = useState('');

  const loadPlans = async () => {
    const token = localStorage.getItem('worker_session_token');
    if (!token) return;
    setIsLoadingPlans(true);
    setApiPlansError('');
    try {
      const data = await getSubscriptionPlans(token);
      if (data.ok && Array.isArray(data.plans)) {
        setApiPlans(data.plans);
      } else {
        setApiPlansError(data.error?.message || data.error || 'Failed to load plans');
        setApiPlans([]);
      }
    } catch (e: any) {
      console.error(e);
      setApiPlansError(e.message || 'System error while loading plans');
      setApiPlans([]);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handleCreatePaymentIntent = async () => {
    if (!selectedRenewalPlanId) return;
    const token = localStorage.getItem('worker_session_token');
    const tenantId = localStorage.getItem('worker_tenant_id');
    if (!token || !tenantId) return;

    setIsUploadingSlip(true);
    setSubmitError('');
    try {
      const data = await createPaymentIntent(token, tenantId, selectedRenewalPlanId);
      if (data.ok) {
        setPaymentIntent(data);
        setRenewalStep(2);
      } else {
        alert(data.error?.message || 'Error creating payment intent');
      }
    } catch (e: any) {
      alert(e.message || 'System error');
    } finally {
      setIsUploadingSlip(false);
    }
  };

  const resetSlipUploadInput = () => {
    setSlipFile(null);
    setSlipPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processAndSubmitSlip = async (file: File) => {
    const token = localStorage.getItem('worker_session_token');
    if (!token || !paymentIntent) return;

    if (isUploadingSlip) return; // Prevent double submit
    setIsUploadingSlip(true);
    setSubmitError('');

    try {
      // 1. Extract QR locally
      const qrData = await extractQrFromImage(file);
      if (!qrData) {
        setSubmitError('ไม่พบ QR ในรูปนี้ กรุณาเลือกรูปสลิปใหม่');
        resetSlipUploadInput();
        setIsUploadingSlip(false);
        return;
      }

      const plan = Array.isArray(apiPlans) ? apiPlans.find(p => p.planId === selectedRenewalPlanId) : null;
      const amount = plan ? plan.price : 0;

      const data = await verifyPaymentSlip(token, paymentIntent.subscriptionId, file, qrData, amount);
      if (data.ok) {
        setIsRenewalCompleted(true);
      } else {
        if (data.error?.code === 'DUPLICATE_TRANSACTION_REF') {
          setSubmitError('สลิปนี้ถูกใช้แล้ว กรุณาเลือกรูปสลิปใหม่');
          resetSlipUploadInput();
        } else {
          setSubmitError(data.error?.message || 'Error submitting slip');
          resetSlipUploadInput();
        }
      }
    } catch (e: any) {
      if (e.code === 'DUPLICATE_TRANSACTION_REF') {
        setSubmitError('สลิปนี้ถูกใช้แล้ว กรุณาเลือกรูปสลิปใหม่');
        resetSlipUploadInput();
      } else if (e.code === 'RATE_LIMITED') {
        const retryMsg = e.retryAfterSeconds ? ` (กรุณารอสักครู่ประมาณ ${Math.ceil(e.retryAfterSeconds / 60)} นาที)` : '';
        setSubmitError(e.message + retryMsg);
        // Do NOT reset slip upload input for RATE_LIMITED
      } else {
        setSubmitError(e.message || 'System error');
        resetSlipUploadInput();
      }
    } finally {
      setIsUploadingSlip(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      resetSlipUploadInput();
      return;
    }
    setSlipFile(file);
    const objectUrl = URL.createObjectURL(file);
    setSlipPreview(objectUrl);
    setSubmitError('');

    // Auto submit
    await processAndSubmitSlip(file);
  };

  const extractQrFromImage = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return resolve('');

        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        resolve(code ? code.data : '');
      };
      img.onerror = () => resolve('');
      img.src = URL.createObjectURL(file);
    });
  };

  const handleSubmitMockSlip = async () => {
    if (!slipFile) return;
    await processAndSubmitSlip(slipFile);
  };

  const handleCopyRenewalPromptPay = () => {
    navigator.clipboard.writeText('0935098808');
    setCopiedPromptPay(true);
    setTimeout(() => setCopiedPromptPay(false), 2200);
  };

  const webhookUrl = 'https://your-worker-domain.com/t/{tenantId}/line/webhook';

  // Helper for PromptPay formatting (Masked for privacy)
  const formatPromptPay = (raw: string) => {
    if (!raw) return '-';
    const cleaned = raw.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `XXX-XXX-${cleaned.slice(6)}`;
    } else if (cleaned.length === 13) {
      return `X-XXXX-XXXXX-${cleaned.slice(10, 12)}-${cleaned.slice(12)}`;
    }
    return 'XXX-XXX-XXXX';
  };

  const formatAccountName = (name: string) => {
    if (!name) return '';
    const trimmed = name.trim();
    if (trimmed.length <= 4) return trimmed.charAt(0) + '***';
    const parts = trimmed.split(' ');
    if (parts.length > 1) {
      return `${parts[0]} ${parts[parts.length-1].charAt(0)}***`;
    }
    return `${trimmed.slice(0, 3)}***`;
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const handleCheckLineStatus = () => {
    setIsCheckingLine(true);
    setTimeout(() => {
      setIsCheckingLine(false);
      setIsConnected(true);
    }, 1200);
  };

  const handleSaveLineSettings = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadedSlipName(file.name);
      setIsUploadingSlip(true);
      setIsRenewalCompleted(false);

      // Simulate real-time checking of the bank slip using transaction ID validation
      setTimeout(() => {
        setIsUploadingSlip(false);
        setIsRenewalCompleted(true);
      }, 2000);
    }
  };

  const formatPlanDailyPrice = (price: number, months: number) => {
    const days = months * 30;
    return (price / days).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };


  const buildOwnerScopedE2EPayload = () => {
    const isOwnerB = state.publicDormId && state.publicDormId.includes('B');
    const ownerKey = isOwnerB ? 'MOCK_B' : 'MOCK_A';
    const dormId = state.publicDormId || (isOwnerB ? 'MOCK_DORM_B' : 'MOCK_DORM_A');
    const roomId = isOwnerB ? 'B202' : 'A101';
    const tenantId = isOwnerB ? 'MOCK_TENANT_B' : 'MOCK_TENANT_A';
    const billId = isOwnerB ? 'MOCK_BILL_B' : 'MOCK_BILL_A';

    const payload = {
      schemaVersion: "owner-scoped-e2e-v1",
      source: "setup-wizard",
      e2eLocal: true,
      mock: true,
      ownerKey,
      dormId,
      dormName: state.dormName || (isOwnerB ? 'Mock Dorm B' : 'Mock Dorm A'),
      roomId,
      roomName: roomId,
      tenantId,
      tenantName: isOwnerB ? 'Mock Tenant B' : 'Mock Tenant A',
      billId,
      amount: 4500,
      theme: state.themeId || 'emerald'
    };

    // Use hash for the payload to avoid breaking URL limits and keep it local
    return '#e2ePayload=' + encodeURIComponent(JSON.stringify(payload));
  };

  const handleLoadPayload = async () => {
    setIsFetchingPayload(true);
    setPayloadError('');
    try {
      // Build config locally from the current Wizard state
      const result = buildApartmentRuntimeConfig(state);
      if ('error' in result) {
        setPayloadError(result.error);
        setIntegrationPayload(null);
      } else {
        setIntegrationPayload(result);
      }
    } catch (e: any) {
      setPayloadError(e.message || 'เกิดข้อผิดพลาดในการสร้าง Payload');
      setIntegrationPayload(null);
    } finally {
      setIsFetchingPayload(false);
    }
  };

  const handleCopyPayload = async () => {
    if (!integrationPayload) return;
    try {
      const jsonStr = JSON.stringify(integrationPayload, null, 2);
      await navigator.clipboard.writeText(jsonStr);
      setCopiedPayload(true);
      setTimeout(() => setCopiedPayload(false), 2200);
    } catch (err) {
      alert('ไม่สามารถคัดลอกผ่าน Clipboard API ได้ กรุณาคลุมดำและคัดลอกด้วยตนเอง');
    }
  };

  const isDevMode = typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).has('dev') || localStorage.getItem('HORPLUS_DEV_TOOLS') === '1');

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in font-sans">
      {state.isViewingStaleDraft && (
        <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r-xl shadow-sm animate-pulse">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-rose-600" />
            <h3 className="text-sm font-bold text-rose-800">คำเตือน: โหมดเรียกดูข้อมูลออฟไลน์ / แบบร่าง</h3>
          </div>
          <p className="text-xs text-rose-700 mt-1">
            ไม่สามารถเชื่อมต่อดึงข้อมูลการตั้งค่าบิลหรือโปรไฟล์ล่าสุดจากเซิร์ฟเวอร์ได้ ระบบกำลังแสดงข้อมูลสำรองแบบร่างแทน กรุณาตรวจสอบอินเทอร์เน็ตหรือรีเฟรชหน้าใหม่อีกครั้ง
          </p>
        </div>
      )}

      {/* Upper Status / Greeting Section */}
      <div className="bg-white border border-slate-100 rounded-[32px] p-6 sm:p-8 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
        {/* Subtle decorative color strips */}
        <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-amber-500 via-emerald-500 to-indigo-600" />

        <div className="flex items-center gap-4">
          {state.logoUrl ? (
            <div className="w-14 h-14 rounded-2xl bg-stone-50 border border-slate-100 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
              <img
                src={state.logoUrl}
                alt="Logo"
                className="w-full h-full object-cover"
                style={{ transform: `scale(${Math.min(3.0, Math.max(0.5, state.logoScale || 1.0))})` }}
                referrerPolicy="no-referrer"
              />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-amber-500 to-orange-500 text-white flex items-center justify-center text-xl font-bold shadow-md shadow-orange-100 shrink-0 select-none">
              {state.dormName ? state.dormName.charAt(0) : '🏢'}
            </div>
          )}
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-extrabold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full uppercase tracking-wide">
                หอพักลงทะเบียนพร้อมบริการ
              </span>
              <span className={`text-[11px] font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1 ${state.tenantStatus === 'active' ? 'text-emerald-800 bg-emerald-50 border border-emerald-100' : 'text-slate-800 bg-slate-100 border border-slate-200'}`}>
                <ShieldCheck className="w-3.5 h-3.5" /> {state.tenantStatus === 'active' ? 'ใช้งานจริง' : state.tenantStatus === 'draft' ? 'แบบร่าง' : 'ไม่ทราบสถานะ'}
              </span>
              {/* DEV ONLY MOCK BANNER */}
              {localStorage.getItem('worker_session_token')?.startsWith('local-dev-') && (
                <span className="text-[11px] font-extrabold text-indigo-800 bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 rounded-full uppercase tracking-wide">
                  🚧 Local Preview (ไม่มีบันทึกขึ้นระบบจริง)
                </span>
              )}
            </div>
            <h1 className="text-xl sm:text-3xl font-serif font-black text-amber-950">
              {state.dormName || 'ข้อมูลหอพักของคุณ'}
            </h1>
            <p className="text-xs text-stone-500">
              รหัสหอพักจำลอง: <span className="font-mono font-bold text-stone-700">DORM-{localStorage.getItem('worker_tenant_id') ? localStorage.getItem('worker_tenant_id')?.substring(0,6).toUpperCase() : '866729'}</span> • จังหวัด {state.province || 'กรุงเทพมหานคร'}
            </p>
            {(state.address || state.googleMapUrl) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-stone-600">
                {state.address && (
                  <span className="flex items-center gap-1 bg-stone-50 px-2 py-0.5 border border-stone-150 rounded text-stone-700">
                    <MapPin className="w-3 h-3 text-rose-500" />
                    <b>ที่อยู่:</b> {state.address}
                  </span>
                )}
                {state.googleMapUrl && (
                  <a
                    href={state.googleMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 border border-blue-100 text-blue-700 rounded transition font-bold"
                  >
                    <ExternalLink className="w-3 h-3 text-blue-500" />
                    <span>Google Map 🗺️</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Button: Show system renewal status & Click to renewal plans */}
        {isLiveLicenseEnabled && liveLicenseStatus ? (
          <div className="px-5 py-3.5 rounded-2xl border border-slate-200 bg-white text-slate-700 text-xs font-black flex items-center justify-center gap-1.5 transition duration-150 w-full md:w-auto shadow-sm shrink-0">
            {liveLicenseStatus.status === 'active' && (
              <><CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 shrink-0" /> <span className="text-emerald-700">ระบบทำงานปกติ (Active)</span></>
            )}
            {liveLicenseStatus.status === 'grace' && (
              <><AlertCircle className="w-4.5 h-4.5 text-amber-500 shrink-0" /> <span className="text-amber-700">ใกล้หมดอายุการใช้งาน</span></>
            )}
            {liveLicenseStatus.status === 'expired' && (
              <><X className="w-4.5 h-4.5 text-rose-500 shrink-0" /> <span className="text-rose-700">หมดอายุการใช้งาน</span></>
            )}
            {liveLicenseStatus.status === 'config_missing' && (
              <><Info className="w-4.5 h-4.5 text-slate-500 shrink-0" /> <span className="text-slate-600">ตั้งค่าระบบยังไม่สมบูรณ์</span></>
            )}
            {liveLicenseStatus.status === 'not_found' && (
              <><Info className="w-4.5 h-4.5 text-slate-500 shrink-0" /> <span className="text-slate-600">ยังไม่มีแพ็กเกจ</span></>
            )}
            {(!['active', 'grace', 'expired', 'config_missing', 'not_found'].includes(liveLicenseStatus.status)) && (
              <><Info className="w-4.5 h-4.5 text-slate-500 shrink-0" /> <span className="text-slate-600">ไม่ทราบสถานะ</span></>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setActiveTab('pricing-plans');
              setRenewalStep(1);
            }}
            className="px-5 py-3.5 rounded-2xl border border-amber-200 hover:border-amber-300 bg-amber-50/40 hover:bg-amber-50 text-amber-900 text-xs font-black flex items-center justify-center gap-1.5 transition duration-150 cursor-pointer w-full md:w-auto shadow-sm animate-pulse shrink-0"
          >
            <Award className="w-4.5 h-4.5 text-amber-600 shrink-0" />
            <span>ระบบใช้งานได้อีก ( 30 วัน ) - คลิกเพื่อต่ออายุ</span>
          </button>
        )}
      </div>

      {/* Next Actions Cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-indigo-500" />
            ขั้นตอนถัดไป (Quick Actions)
          </h3>
          {actionMessage && (
            <span className="text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full animate-fade-in border border-amber-200">
              {actionMessage}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {fallbackAdminUrl ? (
            <div className="p-4 rounded-2xl border border-indigo-200 bg-indigo-50 text-left space-y-2 relative overflow-hidden">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700">
                <Building2 className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-indigo-900 text-sm">จัดการหอพัก</div>
                <div className="text-[10px] text-rose-600 mt-0.5 font-medium">เปิดแท็บอัตโนมัติไม่สำเร็จ</div>
              </div>
              <div className="flex gap-1.5 mt-1.5 relative z-10">
                <button onClick={() => { const p = window.open(fallbackAdminUrl, "_blank"); if (p) { p.opener = null; setFallbackAdminUrl(null); } }} className="px-2.5 py-1 bg-indigo-600 text-white rounded text-[10px] font-bold hover:bg-indigo-700 transition-colors">เปิดอีกครั้ง</button>
                <button onClick={(e) => { const btn = e.currentTarget; navigator.clipboard.writeText(fallbackAdminUrl); const old = btn.innerText; btn.innerText = 'คัดลอกแล้ว'; setTimeout(() => btn.innerText = old, 2000); }} className="px-2.5 py-1 bg-white text-indigo-700 border border-indigo-200 rounded text-[10px] font-bold hover:bg-indigo-50 transition-colors w-16 text-center">คัดลอกลิงก์</button>
              </div>
              <div className="absolute top-3 right-3 text-[9px] font-extrabold text-amber-600 bg-amber-100/80 px-1.5 py-0.5 rounded border border-amber-200/50">Blocked</div>
            </div>
          ) : (
            <button
              onClick={handleOpenAdminV2}
              disabled={adminTokenLoading}
              className="p-4 rounded-2xl border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition text-left space-y-2 group relative overflow-hidden disabled:opacity-60 disabled:cursor-wait"
            >
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 group-hover:scale-110 transition-transform">
                <Building2 className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-indigo-900 text-sm">จัดการหอพัก</div>
                <div className="text-[10px] text-indigo-700 mt-0.5" title="ผู้มีลิงก์นี้สามารถจัดการหอพักได้">{adminTokenLoading ? 'กำลังเปิด...' : 'เปิด Admin-v2 ด้วยลิงก์ใช้งาน 6 ชม.'}</div>
              </div>
              <div className="absolute top-3 right-3 text-[9px] font-extrabold text-indigo-700 bg-indigo-100/80 px-1.5 py-0.5 rounded border border-indigo-200/50">พร้อมใช้งาน</div>
            </button>
          )}

          <button
            onClick={() => {
              setActionMessage('ฟีเจอร์ "สร้างบิล" อยู่ระหว่างการพัฒนา');
              setTimeout(() => setActionMessage(''), 3000);
            }}
            className="p-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition text-left space-y-2 group relative overflow-hidden"
          >
            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
              <Copy className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-slate-800 text-sm">สร้างบิลรายเดือน</div>
              <div className="text-[10px] text-slate-500 mt-0.5">ออกบิลค่าน้ำค่าไฟ</div>
            </div>
            <div className="absolute top-3 right-3 text-[9px] font-extrabold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">เร็วๆ นี้</div>
          </button>

          {fallbackTenantUrl ? (
            <div className="p-4 rounded-2xl border border-rose-200 bg-rose-50 text-left space-y-2 relative overflow-hidden">
              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-700">
                <User className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-rose-900 text-sm">ดูหน้าผู้เช่า</div>
                <div className="text-[10px] text-rose-600 mt-0.5 font-medium">เปิดแท็บอัตโนมัติไม่สำเร็จ</div>
              </div>
              <div className="flex gap-1.5 mt-1.5 relative z-10">
                <button onClick={() => { const p = window.open(fallbackTenantUrl, "_blank"); if (p) { p.opener = null; setFallbackTenantUrl(null); } }} className="px-2.5 py-1 bg-rose-600 text-white rounded text-[10px] font-bold hover:bg-rose-700 transition-colors">เปิดอีกครั้ง</button>
                <button onClick={(e) => { const btn = e.currentTarget; navigator.clipboard.writeText(fallbackTenantUrl); const old = btn.innerText; btn.innerText = 'คัดลอกแล้ว'; setTimeout(() => btn.innerText = old, 2000); }} className="px-2.5 py-1 bg-white text-rose-700 border border-rose-200 rounded text-[10px] font-bold hover:bg-rose-50 transition-colors w-16 text-center">คัดลอกลิงก์</button>
              </div>
              <div className="absolute top-3 right-3 text-[9px] font-extrabold text-amber-600 bg-amber-100/80 px-1.5 py-0.5 rounded border border-amber-200/50">Blocked</div>
            </div>
          ) : (
            <button
              onClick={handleOpenTenantPreview}
              disabled={tenantPreviewLoading}
              className="p-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition text-left space-y-2 group relative overflow-hidden disabled:opacity-60 disabled:cursor-wait"
            >
              <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 group-hover:scale-110 transition-transform">
                <User className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-slate-800 text-sm">ดูหน้าผู้เช่า</div>
                <div className="text-[10px] text-slate-500 mt-0.5" title="ผู้มีลิงก์นี้สามารถดูหน้าผู้เช่าแบบเจ้าของหอได้">{tenantPreviewLoading ? 'กำลังเปิด...' : 'ตรวจทุกห้องและทุกงวด'}</div>
              </div>
              <div className="absolute top-3 right-3 text-[9px] font-extrabold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">พร้อมใช้งาน</div>
            </button>
          )}

          <button
            onClick={() => setActiveTab('line-bot')}
            className="p-4 rounded-2xl border border-indigo-200 bg-indigo-50/30 hover:bg-indigo-50 transition text-left space-y-2 group cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-indigo-900 text-sm">ตั้งค่า LINE Bot</div>
              <div className="text-[10px] text-indigo-700/70 mt-0.5">เชื่อมต่อรับแจ้งเตือนสลิป</div>
            </div>
            <div className="absolute top-3 right-3 text-[9px] font-extrabold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">เปิดใช้งานแล้ว</div>
          </button>
        </div>
      </div>

      {/* Primary Dashboard Area */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

        {/* LEFT NAV BAR: TABS CONTROLLER (3 Tabs requested as web view menus) */}
        <div className="lg:col-span-3 bg-white border border-slate-100 rounded-[28px] p-4 shadow-sm space-y-2">
          <p className="text-[10px] font-extrabold text-slate-400 px-3 uppercase tracking-wider mb-2">
            เมนูการจัดการหอพัก
          </p>

          {[
            { id: 'status-preview', label: '🌟 สถานะการตั้งค่าหอพัก', desc: 'ตรวจสอบความพร้อมและพรีวิวหน้าจอ', color: 'hover:bg-emerald-50 hover:text-emerald-900', activeClass: 'bg-emerald-50 text-emerald-950 border-l-4 border-emerald-500' },
            { id: 'profile', label: '👤 โปรไฟล์หอพัก', desc: 'ข้อมูลทั่วไป ผังอาคาร อัตราราคาบิล', color: 'hover:bg-amber-50 hover:text-amber-900', activeClass: 'bg-amber-50 text-amber-950 border-l-4 border-amber-500' },
            { id: 'line-bot', label: '🤖 LINE Bot Settings', desc: 'ตั้งค่า Messaging API และ Webhook', color: 'hover:bg-indigo-50 hover:text-indigo-900', activeClass: 'bg-indigo-50 text-indigo-950 border-l-4 border-indigo-500' },
            { id: 'pricing-plans', label: '💎 แพลนใช้งานพรีเมียม', desc: 'อัตราบริการ คุ้มค่า ปลดล็อกระบบ', color: 'hover:bg-rose-50 hover:text-rose-900', activeClass: 'bg-rose-50 text-rose-950 border-l-4 border-rose-500' },
            { id: 'integration-payload', label: '📦 ข้อมูลเตรียม Provision', desc: 'ตรวจสอบ Payload ก่อนส่งต่อโปรเจคหลัก', color: 'hover:bg-slate-100 hover:text-slate-900', activeClass: 'bg-slate-100 text-slate-950 border-l-4 border-slate-600' }
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full text-left px-4 py-3.5 rounded-xl transition duration-150 flex flex-col gap-1 cursor-pointer ${
                  isActive ? tab.activeClass : `text-slate-600 ${tab.color}`
                }`}
              >
                <span className="font-extrabold text-sm">{tab.label}</span>
                <span className="text-[10px] opacity-75">{tab.desc}</span>
              </button>
            );
          })}
        </div>

        {/* RIGHT CONTAINER: DYNAMIC CONTENT PANEL */}
        <div className="lg:col-span-9 bg-white border border-slate-100 rounded-[32px] p-6 sm:p-8 shadow-sm min-h-[500px]">

          {/* TAB 1: OWNER DORM PROFILE */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="border-b border-stone-100 pb-4 space-y-1">
                <div className="flex items-center gap-1.5 text-amber-900 font-bold select-none text-sm">
                  <User className="w-4 h-4 text-amber-600" />
                  <span>ประวัติเจ้าของหอพักบุคคลจริง</span>
                </div>
                <p className="text-xs text-slate-400">
                  รายละเอียดอัตรา ค่าน้ำ ค่าไฟ และผังตึกข้อมูลที่เคยลงทะเบียนผ่านระบบ Setup Wizard ร่วมกับผู้เช่า
                </p>
              </div>

              {/* Bento Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* 1. General Info */}
                <div className="bg-stone-50/50 border border-stone-200/60 p-5 rounded-2xl space-y-4 shadow-sm/2">
                  <span className="text-xs font-black text-amber-800 uppercase tracking-wider block border-b border-stone-200 pb-2 flex items-center gap-1.5">
                    🏢 ข้อมูลหอพัก
                  </span>

                  <div className="text-xs space-y-3.5 text-stone-605">
                    <div className="flex justify-between border-b border-dashed border-stone-200 pb-1.5">
                      <span className="font-semibold text-slate-500">ชื่อเจ้าของหอพัก:</span>
                      <span className="font-bold text-slate-800 truncate pl-2 max-w-[180px]">{state.ownerName || <span className="text-rose-500">ยังไม่ระบุ</span>}</span>
                    </div>
                    <div className="flex justify-between border-b border-dashed border-stone-200 pb-1.5">
                      <span className="font-semibold text-slate-500">ชื่อหอพักลงทะเบียน:</span>
                      <span className="font-bold text-amber-950 truncate pl-2 max-w-[180px]">{state.dormName || <span className="text-rose-500">ยังไม่ระบุ</span>}</span>
                    </div>
                    <div className="flex justify-between border-b border-dashed border-stone-200 pb-1.5">
                      <span className="font-semibold text-slate-500">จังหวัดที่ตั้งหอพัก:</span>
                      <span className="font-bold text-slate-800 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-rose-500" />
                        {state.province || <span className="text-rose-500">ยังไม่ระบุ</span>}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-slate-500">ประเภทหอพัก:</span>
                      <span className="font-bold text-indigo-700">
                        {state.dormType === 'mix' ? 'หอพักรวม' : state.dormType === 'male' ? 'หอพักชาย' : 'หอพักหญิง'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2. Bill structure rates */}
                <div className="bg-stone-50/50 border border-stone-200/60 p-5 rounded-2xl space-y-4 shadow-sm/2">
                  <span className="text-xs font-black text-emerald-800 uppercase tracking-wider block border-b border-stone-200 pb-2 flex items-center gap-1.5">
                    📊 ค่าใช้จ่ายพื้นฐาน
                  </span>

                  <div className="text-xs space-y-3.5 text-stone-605">
                    <div className="flex justify-between border-b border-dashed border-stone-200 pb-1.5">
                      <span className="font-semibold text-slate-500">ค่าไฟฟ้า:</span>
                      <span className="font-bold text-slate-800 flex items-center gap-1">
                        <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                        {state.elecRate} {getElectricityUnitLabel(state.elecRateType)}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-dashed border-stone-200 pb-1.5">
                      <span className="font-semibold text-slate-500">ค่าน้ำประปา:</span>
                      <span className="font-bold text-slate-800 flex items-center gap-1">
                        <Droplet className="w-3.5 h-3.5 text-blue-500 fill-blue-100" />
                        {parseFloat(state.waterRatePrice) || 0} {getWaterUnitLabel(state.waterRateType)}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-dashed border-stone-200 pb-1.5">
                      <span className="font-semibold text-slate-500">ค่าอินเทอร์เน็ต Wi-Fi:</span>
                      <span className="font-bold text-slate-800">
                        {state.hasWifiFee ? `${parseFloat(state.wifiFeePrice) || 0} ${getPersonRoomUnitLabel(state.wifiFeeType || state.wifiRateType || state.internetFeeType)}` : 'ไม่มีค่า Wi-Fi'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-slate-500">ค่าส่วนกลาง:</span>
                      <span className="font-bold text-slate-800">
                        {state.hasCommonFee ? `${parseFloat(state.commonFeePrice) || 0} ${getPersonRoomUnitLabel(state.commonFeeType || state.commonRateType)}` : 'ไม่มีค่าส่วนกลาง'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 3. Account Direct PromptPay */}
                <div className="bg-indigo-50/25 border border-indigo-100 p-5 rounded-2xl space-y-4 shadow-sm/2">
                  <span className="text-xs font-black text-indigo-900 uppercase tracking-wider block flex items-center gap-1.5 border-b border-indigo-100 pb-2">
                    <CreditCard className="w-4 h-4 text-indigo-600" />
                    ข้อมูลรับชำระเงิน
                  </span>

                  <div className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-4 text-stone-605">
                    <div className="bg-white p-4 rounded-xl border border-slate-150">
                      <p className="text-[10px] text-slate-400 uppercase font-black">PromptPay ที่ลงทะเบียน</p>
                      <p className="text-base font-mono font-bold text-indigo-950 mt-1.5">
                        {state.promptPayNumber || <span className="text-rose-500 text-sm">ยังไม่ระบุ PromptPay</span>}
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-slate-150">
                      <p className="text-[10px] text-slate-400 uppercase font-black">ชื่อบัญชีผู้รับเงิน</p>
                      <p className="text-base font-bold text-indigo-950 mt-1.5 truncate">
                        {state.accountName || <span className="text-rose-500 text-sm">ยังไม่ระบุชื่อบัญชี</span>}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 4. Logo / Theme Status */}
                <div className="bg-emerald-50/25 border border-emerald-100 p-5 rounded-2xl space-y-4 shadow-sm/2">
                  <span className="text-xs font-black text-emerald-900 uppercase tracking-wider block flex items-center gap-1.5 border-b border-emerald-100 pb-2">
                    <Palette className="w-4 h-4 text-emerald-600" />
                    สถานะโลโก้/ธีม
                  </span>

                  <div className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-4 text-stone-605">
                    <div className="bg-white p-4 rounded-xl border border-slate-150 flex flex-col justify-start min-w-0 overflow-hidden">
                      <p className="text-[10px] text-slate-400 uppercase font-black">โลโก้หอพัก</p>
                      <p className="text-sm font-bold text-emerald-950 mt-1.5">
                        {state.logoUrl && !state.logoUrl.startsWith('blob:') ? 'อัปโหลดสำเร็จ ✓' : <span className="text-rose-500 text-sm">ไม่ได้อัปโหลดโลโก้</span>}
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-slate-150 flex flex-col justify-start min-w-0 overflow-hidden">
                      <p className="text-[10px] text-slate-400 uppercase font-black">สีธีมที่เลือก</p>
                      <p className="text-sm font-bold text-emerald-950 mt-1.5 capitalize truncate flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full inline-block border border-slate-200 shrink-0" style={{ backgroundColor: state.themeId === 'ocean' ? '#0ea5e9' : state.themeId === 'pink' ? '#ec4899' : state.themeId === 'purple' ? '#a855f7' : state.themeId === 'gold' ? '#eab308' : state.themeId === 'slate' ? '#64748b' : state.themeId === 'black' ? '#0f172a' : '#10b981' }}></span>
                        {state.themeId || 'emerald (ค่าเริ่มต้น)'}
                      </p>
                    </div>
                  </div>
                </div>

              </div>

              {/* 4. Registered structures */}
              <div className="bg-slate-50 border border-slate-205 rounded-2xl p-5 space-y-4">
                <span className="text-xs font-bold text-slate-500 block leading-tight border-b border-slate-200 pb-2">
                  🏫 ห้องพักและตึก ({state.buildingsList.length} ตึกสถิติลงทะเบียน และรวมยูนิตห้องพักทั้งหมด {state.rooms.length} ห้อง)
                </span>

                {state.buildingsList && state.buildingsList.length > 0 ? (
                  <div className="flex flex-wrap gap-2 pb-1 bg-white border border-slate-150 p-3 rounded-2xl">
                    {state.buildingsList.map((b: any) => {
                        const totalRooms = b.totalRooms ?? (b.floors * b.roomsPerFloor);
                        const isEven = totalRooms % (b.floors || 1) === 0;
                        const perFloor = isEven ? Math.round(totalRooms / (b.floors || 1)) : null;
                        return (
                          <span key={b.id} className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 inline-flex items-center gap-1.5 text-xs font-bold text-slate-750">
                            <Building2 className="w-3.5 h-3.5 text-amber-600" />
                            <span>
                              ตึก {b.prefix} ({b.floors} ชั้น{perFloor ? ` / ชั้นละ ${perFloor} ห้อง` : ''} / รวม {totalRooms} ห้อง)
                            </span>
                          </span>
                        );
                      })}
                  </div>
                ) : state.rooms && state.rooms.length > 0 ? (
                  <div className="text-xs text-slate-500 bg-slate-100 px-3 py-2 rounded-xl font-bold">
                    พบข้อมูลห้องพักทั้งหมด {state.rooms.length} ห้อง (ไม่มีการระบุแยกตึก)
                  </div>
                ) : (
                  <div className="text-xs text-slate-400 pb-1">
                    ⚠️ ยังไม่มีข้อมูลผังตึกสำเร็จรูป (เขียนรวมจากตัวเลือกเพิ่มเป็นห้องย่อย)
                  </div>
                )}

                {/* mini room collections grid */}
                <div className="space-y-2">
                  <span className="text-[11px] text-slate-400 font-bold block">ผังรายชื่อเลขห้องพัก (คลิกบันทึกแล้ว):</span>

                  <div className="bg-white border border-slate-150 rounded-xl p-3 max-h-48 overflow-y-auto w-full grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {state.rooms.map((rm, i) => (
                      <div key={i} className="px-2.5 py-1.5 rounded mb-0.5 bg-slate-50 text-slate-700 text-[11px] font-bold font-mono text-center border border-slate-100 flex items-center justify-center gap-1.5 truncate">
                        <DoorClosed className="w-3 h-3 text-slate-400 shrink-0" />
                        <span>{rm.roomName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: LINE BOT SETUP */}
          {activeTab === 'line-bot' && (
            <div className="space-y-6">

              <div className="border-b border-stone-100 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-indigo-900 font-bold select-none text-sm animate-fade-in">
                    <MessageSquare className="w-4 h-4 text-indigo-600" />
                    <span>เชื่อมต่อ LINE OA Messaging API</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    เมื่อผู้เช่าทักหา บอทจะอัตโนมัติแจ้งบิล ส่งสลิป และตรวจสอบ PromptPay สลิปด้วยระบบ AI
                  </p>
                </div>

                {/* Sub-tabs switch settings vs instructions */}
                <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setLineSubTab('settings')}
                    className={`flex-1 sm:flex-none px-4 py-2 text-center text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      lineSubTab === 'settings'
                        ? 'bg-white text-indigo-950 shadow-sm border border-slate-250/10'
                        : 'text-slate-500 hover:text-slate-850'
                    }`}
                  >
                    ⚙️ ตั้งค่าคีย์บอท
                  </button>
                  <button
                    type="button"
                    onClick={() => setLineSubTab('instructions')}
                    className={`flex-1 sm:flex-none px-4 py-2 text-center text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      lineSubTab === 'instructions'
                        ? 'bg-white text-indigo-950 shadow-sm border border-slate-250/10'
                        : 'text-slate-500 hover:text-slate-850'
                    }`}
                  >
                    📖 คู่มือละเอียด
                  </button>
                </div>
              </div>

              {/* Settings Area */}
              {lineSubTab === 'settings' && (
                <div className="space-y-6">

                  {/* Status Banner */}
                  <div className={`p-4 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs transition-all duration-300 ${
                    isConnected
                      ? 'bg-emerald-50/70 border-emerald-100 text-emerald-950'
                      : 'bg-slate-50 border-slate-150 text-slate-500'
                  }`}>
                    <div className="flex items-center gap-3">
                      <span className="relative flex h-3 w-3 shrink-0">
                        {isConnected && (
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        )}
                        <span className={`relative inline-flex rounded-full h-3 w-3 ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`}></span>
                      </span>

                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1 text-[11px] sm:text-xs">
                          <span className="font-bold">เชื่อมต่อแชทสำเร็จ</span>
                          <span className="text-emerald-700 block sm:inline font-bold">✅</span>
                        </div>
                        <p className="font-black text-slate-800 text-sm tracking-wide">
                          บอทแอดมิน LINE OA • พร้อมเชื่อมเซิร์ฟเวอร์
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={isCheckingLine}
                      onClick={handleCheckLineStatus}
                      className="w-full sm:w-auto px-4 py-2.5 bg-white border border-slate-205 hover:bg-slate-50 text-slate-705 font-black rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer shadow-sm text-xs shrink-0"
                    >
                      {isCheckingLine ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                          <span>กำลังทดสอบเช็กสัญญาณ...</span>
                        </>
                      ) : (
                        <>
                          <span>🔍 ทดสอบการเชื่อมต่อใหม่</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Channel ID & Secret inputs */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                        <span>🎛️ LINE Channel ID</span>
                        <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={channelId}
                        onChange={(e) => setChannelId(e.target.value)}
                        placeholder="YOUR_LINE_CHANNEL_ID"
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-850 font-bold bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-150 transition"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                        <span>🔑 LINE Channel Secret</span>
                        <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showChannelSecret ? 'text' : 'password'}
                          value={channelSecret}
                          onChange={(e) => setChannelSecret(e.target.value)}
                          placeholder="บอร์ดข้อมูล Channel Secret"
                          className="w-full border border-slate-200 rounded-xl pl-4 pr-10 py-3 text-xs text-slate-850 font-bold bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-150 transition"
                        />
                        <button
                          type="button"
                          onClick={() => setShowChannelSecret(!showChannelSecret)}
                          className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-650 cursor-pointer"
                        >
                          {showChannelSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* OAuth System Notice */}
                  <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-xs text-slate-650 leading-relaxed flex items-start gap-2.5">
                    <span className="text-amber-500 font-bold text-sm shrink-0">💡</span>
                    <p>
                      ระบบจำลองใช้<b> OAuth 2.0 </b> ดึง token ล็อกอินอัตโนมัติจาก ChannelID เพื่อความปลอดภัยและหลีกเลี่ยง token ขาดช่วงอายุ
                    </p>
                  </div>

                  {/* Webhook API Section */}
                  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-150 space-y-3">
                    <label className="text-xs font-extrabold text-slate-700 block">
                      🔗 ลิงก์ปลายทางข้อมูล (Webhook URL สำหรับผู้พัฒนา LINE OA Webhook)
                    </label>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          readOnly
                          value={webhookUrl}
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono text-stone-600 bg-white select-all leading-normal"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyWebhook}
                        className={`px-5 py-3 rounded-xl text-xs font-extrabold transition flex items-center justify-center gap-1.5 cursor-pointer shrink-0 ${
                          copied
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-200 hover:bg-slate-300 text-slate-750'
                        }`}
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            <span>คัดลอกสำเร็จแล้ว</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            <span>คัดลอกลิงก์</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Action Save Buttons */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleSaveLineSettings}
                      className="w-full py-4 bg-[#f65a12] hover:bg-[#e04f0d] text-white font-black text-sm rounded-2xl transition flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-orange-100/60"
                    >
                      <Save className="w-4.5 h-4.5" />
                      <span>{saveSuccess ? 'อัปเดตการตั้งค่าบอทหลักเรียบร้อย!' : 'บันทึกอัปเดตระบบ LINE Bot'}</span>
                    </button>
                  </div>

                </div>
              )}

              {/* Subtab 2: Instructions */}
              {lineSubTab === 'instructions' && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3.5 text-xs text-stone-705 leading-relaxed flex items-start gap-2.5">
                    <span className="text-amber-600 shrink-0 font-bold">💡</span>
                    <p>
                      ทำขั้นตอนเหล่านี้เพื่อเชื่อมบอท LINE กับ<b>ระบบตรวจสอบสลิปอัตโนมัติหอพัก</b> — จะทำงานตรวจสลิปให้ทันทีเมื่อผู้เช่าส่งรูปภาพในช่องแชท
                    </p>
                  </div>

                  {/* Step layout index cards */}
                  <div className="space-y-3.5 max-h-96 overflow-y-auto pr-1">
                    {[
                      {
                        step: 1,
                        text: (
                          <>
                            เปิดหน้าต่างเว็บแชททางการ <b>LINE Official Account Developer Console</b>
                            <a href="https://manager.line.biz" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline ml-1 font-bold">
                              manager.line.biz
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </>
                        )
                      },
                      {
                        step: 2,
                        text: (
                          <>
                            กดปุ่ม ⚙️ <b>การตั้งค่า (Settings)</b> บริเวณด้านมุมบนขวาของหน้าต่าง
                          </>
                        )
                      },
                      {
                        step: 3,
                        text: (
                          <>
                            เลือก <b>Messaging API</b> ที่แถบเมนูด้านซ้ายเพื่อเริ่มระบุ Webhook
                          </>
                        )
                      },
                      {
                        step: 4,
                        text: (
                          <>
                            คัดลอกค่า <b>แชนแนล ID (Channel ID)</b> และ <b>ความลับแชนแนล (Channel Secret)</b> นำมาใส่ในช่องกรอกของพวกเรา
                          </>
                        )
                      },
                      {
                        step: 5,
                        text: (
                          <>
                            นำ Webhook URL นี้ไปวางใน LINE OA Console และกด <b>"เปิดใช้ Webhook (Use Webhook)"</b> เพื่อเปิดสิทธิ์
                            <div className="bg-amber-50 rounded-lg p-2.5 mt-2 font-mono text-[11px] break-all select-all border border-slate-100 flex items-center justify-between text-slate-650">
                              <span>{webhookUrl}</span>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCopyWebhook();
                                }}
                                className="ml-1 bg-white hover:bg-slate-100 px-2 py-1 rounded border border-slate-205 text-indigo-600 font-sans font-bold text-xs"
                              >
                                {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
                              </button>
                            </div>
                          </>
                        )
                      },
                      {
                        step: 6,
                        text: (
                          <>
                            กลับมาที่ระบบของเรา &rarr; กด <b>บันทึกอัปเดตระบบ LINE Bot</b> &rarr; กด 🔍 <b>ทดสอบการเชื่อมต่อใหม่</b> เป็นเสร็จสิ้นระบบ 🎉
                          </>
                        )
                      }
                    ].map((item) => (
                      <div key={item.step} className="p-4 bg-white border border-slate-150 rounded-2xl flex gap-3.5 text-xs text-slate-705">
                        <span className="w-6 h-6 rounded-full bg-indigo-505 bg-amber-500 text-white font-black text-xs flex items-center justify-center shrink-0">
                          {item.step}
                        </span>
                        <div className="leading-relaxed">
                          {item.text}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 text-xs text-emerald-950 flex gap-2">
                    <span className="text-emerald-600 shrink-0 font-bold">✓</span>
                    <p className="font-bold leading-normal">
                      แจ้งเตือนสลิปเช่าเรียบร้อยแล้ว! ทุกสลิปจะได้รับการตรวจเลขที่บัญชี ชื่อเจ้าของ ยอดโอน และสถานะความปลอดภัยสูงสุด
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

              {/* TAB 3: PRICING PLANS */}
          {activeTab === 'pricing-plans' && (() => {
            // Merge API plans with local presentation decorators (MOCK / DEV UI reused)
            const displayPlans = apiPlans;

            const currentPlan = selectedRenewalPlanId ? displayPlans.find(p => p.planId === selectedRenewalPlanId) : null;

            return (
              <div className="space-y-6">
                <div className="border-b border-stone-100 pb-4 space-y-1">
                  <div className="flex flex-col gap-2 mb-4">
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg">
                      <h4 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> สถานะแพ็กเกจ / การใช้งาน (Staging Demo)
                      </h4>
                      <ul className="text-xs text-amber-700 mt-2 space-y-1 list-disc list-inside">
                        <li>ตอนนี้อยู่ในโหมด Staging Demo</li>
                        <li>ยังไม่ใช่ Production และยังไม่เปิดรับเงินจริง</li>
                        <li>ฟีเจอร์ Admin/Tenant Preview, LINE Flex Preview ใช้งานได้ในโหมดจำลอง</li>
                        <li>การส่ง LINE จริง / SlipOK / Payment จะต้องเปิดผ่านขั้นตอนอนุมัติแยกเท่านั้น (ต้องผ่าน GO Checklist)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-rose-900 font-bold select-none text-base">
                    <Award className="w-5 h-5 text-rose-600 shrink-0" />
                    <span className="font-serif font-black">ต่ออายุสัญญาการเช่าใช้งานระบบ (Demo Pricing)</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    เลือกและยืนยันแพ็คเกจต่ออายุสัญญาระบบของคุณเพื่อทดสอบ UI (ไม่มีการเรียกเก็บเงินจริง)
                  </p>
                </div>

                {renewalStep === 1 ? (
                  /* STEP 1: SELECT PLAN */
                  <div className="space-y-6">

                    <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r-lg mb-6 shadow-sm">
                      <h4 className="text-emerald-900 font-bold mb-2 flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-600" /> สถานะแพ็กเกจของหอนี้ (1 บัญชี Google = 1 หอพัก)
                      </h4>
                      {licenseStatus ? (
                        <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                          <div>
                            <span className="text-slate-500">สถานะจากระบบ License กลาง: </span>
                            <span className="font-bold text-emerald-800 uppercase px-2 py-0.5 bg-emerald-100 rounded text-xs">{licenseStatus.status}</span>
                            {licenseStatus.status === 'staging_demo' && <span className="text-xs text-rose-500 ml-2 font-bold">(Staging Demo / ยังไม่เปิด Production)</span>}
                          </div>
                          <div>
                            <span className="text-slate-500">แพ็กเกจปัจจุบัน: </span>
                            <span className="font-bold text-slate-800">{licenseStatus.planName || 'ยังไม่พบข้อมูล'}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">สถานะ Production: </span>
                            <span className="font-bold text-rose-600">{licenseStatus.productionEnabled ? 'เปิดใช้งานแล้ว' : 'ปิดการใช้งานชั่วคราว'}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">ต่ออายุชำระเงิน: </span>
                            <span className="font-bold text-rose-600">{licenseStatus.renewalAvailable ? 'เปิดให้ต่ออายุ' : 'ยังไม่เปิดต่ออายุจริง (ยังไม่เปิดรับเงินจริง)'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500 italic">กำลังดึงข้อมูลสถานะ...</div>
                      )}
                    </div>

                      <div className="text-xs font-bold text-slate-650 bg-slate-50 border border-slate-150 rounded-xl px-4 py-3">
                      👉 แพ็คเกจจากระบบกลาง (ราคาเดียวกันทุกหอพัก, 1 บัญชี Google ใช้งานได้ 1 หอพัก)
                    </div>

                    {apiPlansError && (
                      <div className="text-xs text-rose-600 font-bold bg-rose-50 p-3 rounded-xl border border-rose-100">
                        {apiPlansError}
                      </div>
                    )}

                    {isLoadingPlans ? (
                      <div className="flex justify-center py-10">
                        <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
                      </div>
                    ) : displayPlans.length === 0 && !apiPlansError ? (
                      <div className="text-center py-10 text-slate-400 text-xs font-bold border-2 border-dashed border-slate-100 rounded-xl">
                        ไม่พบแพ็คเกจในขณะนี้
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                        {displayPlans.map((plan) => {
                          const isActive = selectedRenewalPlanId === plan.planId;
                          const originalPrice = plan.oldPrice || plan.price;
                          const saving = originalPrice - plan.price;
                          const percent = originalPrice > 0 ? Math.round((saving / originalPrice) * 100) : 0;
                          const dailyPrice = formatPlanDailyPrice(plan.price || 0, plan.months || 1);

                          return (
                            <div
                              key={plan.planId}
                              onClick={() => setSelectedRenewalPlanId(plan.planId)}
                              className={`relative border-2 rounded-2xl p-2.5 sm:p-4 text-center cursor-pointer transition-all duration-300 flex flex-col items-center justify-between min-h-[260px] select-none overflow-hidden ${
                                isActive
                                  ? "border-emerald-600 bg-emerald-50/10 shadow-md ring-1 ring-emerald-500 scale-[1.01]"
                                  : "border-slate-150 bg-white hover:border-emerald-400 hover:translate-y-[-2px] hover:shadow-sm"
                              }`}
                            >
                              {/* Recommended/Special Tilted Ribbon Badge like the image */}
                              {plan.badge && (
                                <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none select-none overflow-hidden z-10">
                                  <div className={`absolute top-[18px] right-[-26px] w-[105px] text-[8.5px] font-black text-white py-1 text-center rotate-45 shadow-sm transform tracking-wider select-none ${
                                    plan.badge.includes("แนะนำ")
                                      ? "bg-[#1f6d4d]"
                                      : plan.badge.includes("คุ้มค่า")
                                        ? "bg-[#166534]"
                                        : "bg-[#22c55e]"
                                  }`}>
                                    {plan.badge}
                                  </div>
                                </div>
                              )}

                              <div className="pt-2 w-full text-center">
                                <div className="text-[15px] font-black text-slate-900 leading-tight">{plan.label || 'Unknown'}</div>
                                <div className="text-[9px] font-bold text-slate-400 tracking-wider uppercase mt-1.5">{plan.tag || 'MOCK'}</div>
                              </div>

                              <div className="text-slate-500 text-[10px] leading-relaxed my-3 flex-grow flex items-center justify-center text-center">
                                {plan.desc || 'Dev test pricing plan'}
                              </div>

                              <div className="w-full mt-auto pt-2.5 border-t border-dashed border-slate-100 flex flex-col items-center">
                                <div className="text-[10px] text-slate-400 line-through">
                                  {(originalPrice || 0).toLocaleString("th-TH")}.-
                                </div>
                                <div className="text-lg font-black text-emerald-600 mt-0.5">
                                  {(plan.price || 0).toLocaleString("th-TH")}.-
                                </div>
                                <div className="text-[9px] font-black text-emerald-800 bg-emerald-50/80 border border-emerald-100 px-1.5 py-0.5 rounded-md mt-1 shrink-0">
                                  ลด {percent}% (ประหยัด {saving.toLocaleString("th-TH")}.-)
                                </div>
                                <div className="text-[10px] text-slate-400 mt-2 font-bold font-mono">
                                  {dailyPrice}.- / วัน
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex gap-3 justify-end pt-2">
                      <button
                        type="button"
                        onClick={() => setSelectedRenewalPlanId(null)}
                        className="px-5 py-3 rounded-2xl border border-slate-200 hover:border-slate-350 bg-white text-slate-600 text-xs font-bold transition cursor-pointer"
                      >
                        ยกเลิก
                      </button>
                      <button
                        type="button"
                        disabled={!selectedRenewalPlanId || isUploadingSlip}
                        onClick={() => {}}
                        className="px-6 py-3 rounded-2xl bg-emerald-700 hover:bg-emerald-850 active:bg-emerald-900 text-white text-xs font-black transition cursor-pointer shadow-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        {isUploadingSlip ? (
                          <><RefreshCw className="w-4 h-4 animate-spin" /> กำลังสร้าง...</>
                        ) : (
                          <span>สร้างคำสั่งซื้อ ➔</span>
                        )}
                      </button>
                    </div>

                  </div>
                ) : (
                  /* STEP 2: SCANNED PROMPTPAY & SLIP UPLOAD */
                  currentPlan && (
                    <div className="space-y-6">

                      {/* Summary Banner bar */}
                      <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl px-4 py-3 text-xs sm:text-sm font-black text-emerald-950 text-center animate-fade-in">
                        แพ็คเกจที่เลือก: <span className="text-emerald-800 font-extrabold">{currentPlan.label}</span> ({currentPlan.price.toLocaleString("th-TH")}.-)
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

                        {/* Left Column: QR Code Promptpay payment */}
                        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-5 flex flex-col items-center gap-4 text-center opacity-80 pointer-events-none">
                          <div className="w-40 h-40 bg-slate-200 p-2 border border-slate-300 rounded-xl shadow-sm flex items-center justify-center text-slate-500 font-bold text-xs">
                            [QR ซ่อนอยู่ในโหมด Demo]
                          </div>

                          <div className="w-full space-y-2 text-xs">
                            <div className="text-slate-800 font-extrabold text-xs sm:text-sm mb-1">
                              ชำระค่าต่ออายุระบบ (ระงับชั่วคราว)
                            </div>

                            <div className="flex justify-between items-baseline py-1.5 border-b border-slate-200/50">
                              <span className="text-slate-400 font-semibold">ยอดชำระ:</span>
                              <span className="text-sm font-black text-slate-500">
                                {currentPlan.price.toLocaleString("th-TH")} บาท (ข้อมูลจำลอง)
                              </span>
                            </div>

                            <div className="flex justify-between items-center py-1.5 border-b border-slate-200/50">
                              <span className="text-slate-400 font-semibold">PromptPay:</span>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono font-bold text-slate-500">
                                  [ซ่อนข้อมูล]
                                </span>
                              </div>
                            </div>

                            <div className="flex justify-between items-baseline py-1.5">
                              <span className="text-slate-400 font-semibold">ชื่อบัญชี:</span>
                              <span className="font-bold text-slate-500">
                                [ซ่อนข้อมูล]
                              </span>
                            </div>

                            <div className="text-[10px] text-amber-600 font-bold mt-2 bg-amber-50 p-2 rounded">
                              ฟังก์ชันชำระเงินถูกปิดกั้น (Blocked) ในโหมด Staging
                            </div>
                          </div>
                        </div>

                        {/* Right Column: Slip upload container */}
                        <div className="space-y-4">
                          <div className="bg-white border-2 border-slate-200 rounded-2xl p-4 sm:p-5">
                            <div className="text-sm font-black text-slate-800 mb-1">
                              ส่งสลิปจำลอง (MOCK)
                            </div>
                            <div className="text-xs text-slate-500 mb-4">
                              (ระบบ Mock: รองรับการอ่าน QR Code จากรูปภาพสลิป โดยจะดึงข้อมูลมาจำลองการทำงานฝั่งเซิร์ฟเวอร์)
                            </div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/png, image/jpeg, image/webp"
                              onChange={handleFileChange}
                              disabled={isUploadingSlip || isRenewalCompleted}
                              className="w-full text-xs font-medium text-slate-700 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl transition file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                            />
                            {slipPreview && (
                              <div className="mb-4 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 flex justify-center items-center h-48">
                                <img src={slipPreview} alt="Slip preview" className="max-h-full max-w-full object-contain" />
                              </div>
                            )}
                            {submitError && (
                              <div className="text-xs text-rose-600 font-bold bg-rose-50 p-2 rounded-lg mb-3 border border-rose-100">
                                {submitError}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={handleSubmitMockSlip}
                              disabled={!slipFile || isUploadingSlip || isRenewalCompleted}
                              className="w-full px-4 py-3 bg-slate-800 hover:bg-slate-900 disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-xs rounded-xl transition flex items-center justify-center gap-1.5 shadow-sm"
                            >
                              {isUploadingSlip ? 'กำลังตรวจสอบ...' : 'ส่งสลิป ➔'}
                            </button>
                          </div>

                          {/* Upload simulation spinner */}
                          {isUploadingSlip && (
                            <div className="p-4 bg-emerald-50/20 border border-emerald-100 rounded-xl space-y-2.5 animate-pulse">
                              <div className="flex items-center gap-2 text-xs font-bold text-emerald-800">
                                <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
                                <span>กำลังตรวจเช็คสลิปโอนเงินอัจฉริยะอัตโนมัติ...</span>
                              </div>
                              <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 w-2/3" style={{ animation: 'loading-bar 1.5s infinite linear' }} />
                              </div>
                            </div>
                          )}

                          {isRenewalCompleted && (
                            <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-2.5 shadow-sm animate-fade-in text-emerald-950 font-semibold text-xs leading-relaxed">
                              <span className="text-lg">🎉</span>
                              <div className="space-y-0.5">
                                <p className="font-extrabold text-emerald-900 text-[13px]">ต่ออายุการเช่าระบบสำเร็จแล้ว!</p>
                                <p>ยินดีด้วยครับ! ระบบต่ออายุสัญญาใช้งานให้ท่านเรียบร้อยแล้วจำนวน <b>{currentPlan.label}</b> ยอดเงินตรงตามยอดเรียกเก็บ ({currentPlan.price}.-) ได้บันทึกลงในระบบแผงโปรไฟล์หอพักวิศวกรรมเรียบร้อย ขอบพระคุณอย่างสูงครับ!</p>
                              </div>
                            </div>
                          )}

                          <div className="p-3.5 bg-green-50 border-l-4 border-emerald-700 rounded-r-xl text-xs text-green-800 leading-relaxed font-semibold">
                            ⚡️ ระบบต่ออายุอัตโนมัติ หลังจากแนบภาพสลิปโอนเงินแล้ว ระบบจะตรวจสอบความถูกต้องของสลิปและเพิ่มวันใช้งานให้ทันที
                          </div>
                        </div>

                      </div>

                      <div className="flex justify-start">
                        <button
                          type="button"
                          onClick={() => {
                            setRenewalStep(1);
                            setUploadedSlipName(null);
                            setIsRenewalCompleted(false);
                          }}
                          className="px-5 py-3 rounded-2xl border border-slate-200 hover:border-slate-350 bg-white text-slate-600 text-xs font-bold transition cursor-pointer flex items-center gap-1.5"
                        >
                          <span>⬅ ย้อนกลับ</span>
                        </button>
                      </div>

                    </div>
                  )
                )}

              </div>
            );
          })()}

          {/* TAB 4: INTEGRATION PAYLOAD */}
          {activeTab === 'integration-payload' && (
            <div className="space-y-6 animate-fade-in">
              <div className="border-b border-stone-100 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-slate-800 font-bold select-none text-sm">
                    <Package className="w-4 h-4 text-slate-600" />
                    <span>ข้อมูลเตรียม Provision (Integration Payload)</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    ตรวจสอบโครงสร้างข้อมูลหอพักและแพ็กเกจแรกเข้าก่อนส่งต่อไปยังโปรเจคหลัก
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleLoadPayload}
                  disabled={isFetchingPayload}
                  className="w-full sm:w-auto px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold rounded-xl transition flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-70 cursor-pointer"
                >
                  {isFetchingPayload ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  <span>โหลด Payload ล่าสุด</span>
                </button>
              </div>

              {payloadError && (
                <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-start gap-3 shadow-sm">
                  <Info className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-rose-800 font-semibold space-y-1">
                    <p>ไม่สามารถดึงข้อมูลได้</p>
                    <p className="font-normal opacity-90">{payloadError}</p>
                  </div>
                </div>
              )}

              {integrationPayload && (
                <div className="space-y-4">
                  {/* Warning Banner */}
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3 shadow-sm">
                    <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-blue-800 space-y-1">
                      <p className="font-bold">ข้อมูลโครงสร้าง Runtime Config (Local Only)</p>
                      <p className="opacity-90 leading-relaxed">
                        ระบบทำการสรุปข้อมูลทั้งหมดจากฟอร์มให้พร้อมใช้งาน (Handoff) สำหรับโปรเจคหลัก โดยข้อมูลในที่นี้<b>ไม่มีรหัสผ่าน หรือ Secret Keys ใดๆ ทั้งสิ้น</b> รวมถึงฟังก์ชัน SlipVerification ถูกตั้งค่าเป็น Placeholder รอการเชื่อมต่อ API ของจริง
                      </p>
                    </div>
                  </div>

                  {/* Actions & Status Banner */}
                  <div className="p-4 border rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm bg-emerald-50 border-emerald-200 text-emerald-900">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm bg-emerald-100 text-emerald-600">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="text-[11px] uppercase font-black opacity-60">สถานะความพร้อม</p>
                        <p className="text-sm font-bold">
                          พร้อมส่งต่อให้ระบบหลัก (Apartment/Tenant)
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={handleCopyPayload}
                        className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 shrink-0 w-full sm:w-auto shadow-sm cursor-pointer ${
                          copiedPayload
                            ? 'bg-emerald-600 text-white border border-emerald-600'
                            : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        {copiedPayload ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        <span>{copiedPayload ? 'คัดลอกแล้ว' : 'คัดลอก JSON'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadRuntimeConfig(integrationPayload, `runtime-config-${integrationPayload.dorm.name}.json`)}
                        className="px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 shrink-0 w-full sm:w-auto shadow-sm cursor-pointer bg-slate-800 hover:bg-slate-900 text-white"
                      >
                        <span>ดาวน์โหลด .json</span>
                      </button>
                    </div>
                  </div>

                  {/* Next Steps & Safety Notice (Visible when connected) */}
                  {state.apartmentLinked && (
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 shadow-sm space-y-5 relative overflow-hidden mb-4 animate-fade-in">
                      <div className="absolute top-0 inset-x-0 h-1 bg-slate-400" />

                      <div className="space-y-4">
                        <h3 className="text-sm font-black text-slate-800 flex items-center gap-1.5">
                          <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                          ขั้นตอนถัดไป (Next Steps)
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-600">
                          <div className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <span>ตรวจหน้า Admin Preview</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <span>ตรวจหน้า Tenant Preview</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <span>ตรวจโลโก้และธีม</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <span>ตรวจรายการห้องพัก</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <span>ตรวจค่าน้ำ ค่าไฟ ค่าส่วนกลาง</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <span className="font-semibold text-amber-700">ยังไม่ใช่ Production จริง</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 space-y-2">
                        <div className="font-bold flex items-center gap-1.5 text-amber-600">
                          <AlertCircle className="w-4 h-4" /> ลิงก์ Preview สำหรับทดสอบ
                        </div>
                        <ul className="list-disc list-inside space-y-1 ml-1 opacity-90 text-amber-700 text-xs">
                          <li>ลิงก์นี้ใช้สำหรับทดสอบ Staging เท่านั้น ยังไม่ใช่ Production</li>
                          <li>ยังไม่มีการส่ง LINE จริง หรือตรวจ SlipOK จริง</li>
                        </ul>
                      </div>


                    </div>
                  )}

                  {isDevMode && (
                    <>
                      {/* Real Provision UI */}
                      <div className={`bg-white border-2 ${state.apartmentLinked ? 'border-indigo-100' : 'border-emerald-100'} rounded-2xl p-5 shadow-sm space-y-4 relative overflow-hidden mb-4`}>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <h3 className={`text-sm font-black flex items-center gap-1.5 ${state.apartmentLinked ? 'text-indigo-950' : 'text-emerald-950'}`}>
                          {state.apartmentLinked ? (
                            <><span className="text-indigo-600">🛠️</span> เครื่องมือตรวจสอบ Staging สำหรับผู้พัฒนา</>
                          ) : (
                            <><span className="text-emerald-600">🛠️</span> เครื่องมือตรวจสอบ Staging สำหรับผู้พัฒนา</>
                          )}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {state.apartmentLinked
                            ? "ตรวจสอบ Payload ສຳหรับระบบจำลอง (Validate Only)"
                            : "สำหรับตรวจสอบข้อมูลก่อนเข้าสู่ระบบ Staging API"}
                        </p>
                        {state.apartmentLinked && (
                          <div className="text-xs text-indigo-700 opacity-90 pt-1 flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
                            สถานะนี้อ่านจากระบบ staging ล่าสุด
                            <button onClick={() => window.location.reload()} className="ml-2 underline hover:text-indigo-900 transition-colors">หากข้อมูลไม่ตรง ให้กดรีเฟรชสถานะ</button>
                          </div>
                        )}
                        {state.apartmentLinked && (
                          <div className="pt-3 flex flex-col gap-4">
                            {/* Admin CTA */}
                            <div>
                              <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
                                จำลองหน้าจัดการหอพัก (Admin V2 Preview)
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[9px] uppercase font-black rounded border border-amber-200 tracking-wider">Preview / ยังไม่ใช้งานจริง</span>
                              </h4>
                              {fallbackAdminUrl ? (
                                <div className="space-y-1 text-xs">
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => { const p = window.open(fallbackAdminUrl, "_blank"); if (p) { p.opener = null; setFallbackAdminUrl(null); } }} className="px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 hover:bg-amber-700 transition-colors shadow-sm">เปิดอีกครั้ง</button>
                                    <button onClick={(e) => { const btn = e.currentTarget; navigator.clipboard.writeText(fallbackAdminUrl); const old = btn.innerText; btn.innerText = 'คัดลอกแล้ว'; setTimeout(() => btn.innerText = old, 2000); }} className="px-4 py-2 bg-white text-amber-700 border border-amber-200 text-xs font-bold rounded-lg hover:bg-amber-50 transition-colors w-24 text-center">คัดลอกลิงก์</button>
                                  </div>
                                  <p className="text-amber-600 flex items-start gap-1 mt-1.5 max-w-sm text-xs">
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>เปิดแท็บอัตโนมัติไม่สำเร็จ กรุณากดปุ่มเปิดอีกครั้ง หรือคัดลอกลิงก์</span>
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-1 text-xs">
                                  <button
                                    onClick={handleOpenAdminV2}
                                    disabled={adminTokenLoading}
                                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg flex items-center gap-1.5 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-wait w-fit text-sm"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    {adminTokenLoading ? 'กำลังเปิด...' : 'จัดการหอพัก (Admin-v2)'}
                                  </button>
                                  <p className="text-slate-500 flex items-start gap-1 mt-1.5 max-w-sm text-xs">
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>เปิด Admin-v2 ด้วยลิงก์ใช้งาน 6 ชม. (ผู้มีลิงก์นี้สามารถจัดการหอพักได้)</span>
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Tenant Preview CTA */}
                            <div>
                              <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
                                จำลองหน้าผู้เช่า (Tenant V2 Preview)
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[9px] uppercase font-black rounded border border-amber-200 tracking-wider">Preview / Read-only</span>
                              </h4>
                              {(() => {
                                const rawTenantPreviewUrl = tenantPreviewUrl || realProvisionResult?.data?.tenantPreviewUrl;
                                const HORPLUS_APP_BASE_URL = window.location.origin;
                                let safeTenantPreviewUrl = rawTenantPreviewUrl;
                                if (rawTenantPreviewUrl) {
                                  try {
                                    const url = new URL(rawTenantPreviewUrl);
                                    const token = url.searchParams.get('token') || url.searchParams.get('previewToken') || url.searchParams.get('tenantAccessToken');
                                    if (token) {
                                      safeTenantPreviewUrl = `${HORPLUS_APP_BASE_URL}/tenant-v2.html?token=${encodeURIComponent(token)}`;
                                    }
                                  } catch (e) {}
                                }
                                if (safeTenantPreviewUrl) {
                                  return (
                                    <button
                                      onClick={() => window.open(safeTenantPreviewUrl, '_blank', 'noopener,noreferrer')}
                                      className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 hover:bg-emerald-700 transition-colors shadow-sm"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" /> เปิดหน้า Tenant V2 Preview
                                    </button>
                                  );
                                }
                                return (
                                  <div className="space-y-1 text-xs">
                                    <button
                                      onClick={handleOpenTenantPreview}
                                      disabled={tenantPreviewLoading}
                                      className="px-4 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-bold rounded-lg flex items-center gap-1.5 transition-colors border border-emerald-200 disabled:opacity-60 disabled:cursor-wait w-fit"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                      {tenantPreviewLoading ? 'กำลังขอลิงก์...' : 'ขอลิงก์เปิดหน้า Tenant V2 Preview'}
                                    </button>
                                    <p className="text-slate-500 flex items-start gap-1 mt-1.5 max-w-sm">
                                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                      <span>ระบบจำลองบิลและหน้าต่างของผู้เช่าอย่างปลอดภัย (ไม่มีการส่งไลน์จริง)</span>
                                    </p>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                      {(() => {
                        const token = readSessionToken();
                        if (!token) {
                          return (
                            <button disabled className="px-5 py-2.5 bg-slate-300 text-slate-500 text-xs font-black rounded-xl cursor-not-allowed">
                              กรุณาล็อกอินใหม่เพื่อทดสอบ
                            </button>
                          );
                        }
                        if (!integrationPayload) {
                          return (
                            <button disabled className="px-5 py-2.5 bg-slate-300 text-slate-500 text-xs font-black rounded-xl cursor-not-allowed">
                              กรุณาเตรียม Payload ก่อน
                            </button>
                          );
                        }
                        return (
                          <button
                            type="button"
                            onClick={handleRealProvision}
                            disabled={realProvisionLoading}
                            className={`px-5 py-2.5 text-xs font-black rounded-xl cursor-pointer transition ${
                              state.apartmentLinked
                                ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200'
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            }`}
                          >
                            {realProvisionLoading ? 'กำลังตรวจสอบ...' : (state.apartmentLinked ? 'ตรวจสอบซ้ำ (Validate Only)' : 'ตรวจสอบความพร้อม HorPlus Runtime')}
                          </button>
                        );
                      })()}
                    </div>
                    {realProvisionResult && (
                      <div className="mt-4 border-t border-slate-100 pt-5">
                        {realProvisionResult.ok ? (
                           <div className="text-emerald-700 text-xs space-y-1">
                             <div className="font-bold">✅ ตรวจสอบสำเร็จ (Validate Only — wouldWrite: {String(realProvisionResult.data?.wouldWrite ?? false)})</div>
                             <div className="text-slate-500">Public Dorm ID: {realProvisionResult.data?.publicDormId || 'N/A'} | Bucket: {realProvisionResult.data?.bucket || 'valid'}</div>
                             <div className="text-amber-700 font-semibold">⚠️ ยังไม่ได้บันทึกข้อมูลจริง — ขั้นตอนนี้เป็นเพียงการตรวจสอบ Payload เท่านั้น</div>
                           </div>
                        ) : (
                           <div className="text-red-600 text-xs">Failed: {realProvisionResult.error} {realProvisionResult.bucket ? `(${realProvisionResult.bucket})` : ''}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mock Provision UI Placeholder (Dev Panel Trigger) */}
                  <div className="bg-white border-2 border-indigo-100 rounded-2xl p-5 shadow-sm space-y-4 relative overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-1 bg-indigo-500" />

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <h3 className="text-sm font-black text-indigo-950 flex items-center gap-1.5">
                          <span className="text-indigo-600">⚡</span> จำลองการสร้างหอพัก (Mock Provision)
                        </h3>
                        <p className="text-xs text-slate-500">
                          ฟีเจอร์สำหรับนักพัฒนาเพื่อทดสอบการเชื่อมต่อ API ไปยัง Endpoint จำลอง (Dev Panel Only)
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={handleOpenMockProvisionDialog}
                        className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black rounded-xl border border-indigo-700 flex items-center justify-center gap-2 cursor-pointer w-full sm:w-auto shrink-0 transition shadow-sm"
                      >
                        <Zap className="w-4 h-4" />
                        <span>เริ่มจำลอง (Dev Only)</span>
                      </button>
                    </div>

                    <div className="bg-indigo-50/50 rounded-xl p-3 border border-indigo-100/50 flex flex-col sm:flex-row items-start gap-3">
                      <div className="bg-white p-1.5 rounded-lg border border-indigo-100 shadow-sm shrink-0 mt-0.5">
                        <Info className="w-4 h-4 text-indigo-500" />
                      </div>
                      <div className="text-xs text-indigo-900 leading-relaxed space-y-1">
                        <p className="font-bold">⚠️ คำเตือนความปลอดภัย (Mock Only)</p>
                        <ul className="list-disc list-inside opacity-90 space-y-0.5 pl-1">
                          <li>ฟีเจอร์นี้<strong>จะไม่ถูกส่งไปบันทึกลงฐานข้อมูลจริง</strong> (No Real Provision / No DB Write)</li>
                          <li><code className="bg-indigo-100/70 text-indigo-800 px-1 py-0.5 rounded">mockDormId</code> ที่ได้รับในอนาคตจะเป็นรหัสจำลองเท่านั้น ห้ามนำไปใช้งานจริง</li>
                          <li>Endpoint และ Token จะไม่ถูกบันทึกลงเครื่องเพื่อความปลอดภัย (No Persistence)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Mock Provision Dialog */}
                  {isMockProvisionDialogOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
                      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-indigo-50/50">
                          <h2 className="text-sm font-black text-indigo-950 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-indigo-600" />
                            Mock Provision Dev Panel
                          </h2>
                          <button
                            type="button"
                            onClick={handleCloseMockProvisionDialog}
                            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="p-5 overflow-y-auto space-y-5">
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                            <div className="text-xs text-amber-900 leading-relaxed">
                              <p className="font-bold mb-1">โหมดนักพัฒนา (Developer Mode)</p>
                              <p>ฟอร์มนี้จะใช้ส่งข้อมูลไปยัง Endpoint จำลองที่ระบุด้วยตัวเอง ห้ามใช้กับ Production Endpoint โดยเด็ดขาด <b>ห้ามใส่ Password หรือ Secret สำคัญของจริงลงไป</b></p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-700">Endpoint URL (Local Worker)</label>
                              <input
                                type="text"
                                value={mockProvisionEndpoint}
                                onChange={(e) => setMockProvisionEndpoint(e.target.value)}
                                placeholder="https://api.horplus.com/admin/proxy/wizard/runtime-config/mock-provision"
                                className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 font-mono"
                              />
                            </div>

                            <div className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-700">Auth Token (Bearer)</label>
                              <input
                                type="password"
                                value={mockProvisionToken}
                                onChange={(e) => setMockProvisionToken(e.target.value)}
                                placeholder="YOUR_TEST_TOKEN"
                                className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 font-mono"
                              />
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer mt-2 select-none group">
                              <div className="relative flex items-center">
                                <input
                                  type="checkbox"
                                  checked={mockProvisionAllowDevEndpoint}
                                  onChange={(e) => setMockProvisionAllowDevEndpoint(e.target.checked)}
                                  className="peer sr-only"
                                />
                                <div className="w-4 h-4 border-2 border-slate-300 rounded peer-checked:bg-rose-500 peer-checked:border-rose-500 transition-colors"></div>
                                <Check className="absolute w-3 h-3 text-white left-0.5 top-0.5 opacity-0 peer-checked:opacity-100 transition-opacity" />
                              </div>
                              <span className="text-xs font-semibold text-rose-700 group-hover:text-rose-800 transition-colors">
                                [อันตราย] อนุญาต Endpoint ที่มีคำว่า workers.dev / production (Dev Override)
                              </span>
                            </label>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-3 pt-2">
                            <button
                              type="button"
                              onClick={handleCallMockProvision}
                              disabled={mockProvisionLoading || !mockProvisionEndpoint || !mockProvisionToken}
                              className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-200 disabled:cursor-not-allowed text-white text-xs font-black rounded-xl transition flex items-center justify-center gap-2 shadow-sm"
                            >
                              {mockProvisionLoading ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Zap className="w-4 h-4" />
                              )}
                              {mockProvisionLoading ? 'กำลังจำลองการส่ง...' : 'เริ่มจำลอง Mock Provision'}
                            </button>

                            <button
                              type="button"
                              onClick={() => setMockProvisionResult(null)}
                              disabled={mockProvisionLoading || !mockProvisionResult}
                              className="px-4 py-3 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed text-slate-600 text-xs font-bold rounded-xl transition"
                            >
                              ล้างผลลัพธ์
                            </button>
                          </div>

                          {/* Result Display */}
                          {mockProvisionResult && (
                            <div className="mt-4 border-t border-slate-100 pt-5 animate-fade-in space-y-3">
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Result Panel</h4>

                              {mockProvisionResult.ok ? (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm space-y-3">
                                  <div className="flex items-center gap-2 text-emerald-800">
                                    <CheckCircle2 className="w-5 h-5" />
                                    <span className="font-black text-sm">การจำลองสำเร็จ (200 OK)</span>
                                    {mockProvisionResult.idempotentReplay && (
                                      <span className="ml-2 px-2 py-0.5 bg-emerald-200 text-emerald-800 rounded text-[10px] font-bold">REPLAY</span>
                                    )}
                                  </div>

                                  <div className="space-y-1 text-xs">
                                    <div className="flex gap-2">
                                      <span className="text-emerald-700/70 w-24">Mock Dorm ID:</span>
                                      <code className="font-mono font-bold text-emerald-900 bg-emerald-100/50 px-1.5 py-0.5 rounded">
                                        {mockProvisionResult.mockDormId || 'N/A'}
                                      </code>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-emerald-700/70 w-24">Storage:</span>
                                      <span className="font-semibold text-emerald-800">{mockProvisionResult.storage}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-emerald-700/70 w-24">Memory Only:</span>
                                      <span className="font-semibold text-emerald-800">{mockProvisionResult.memoryOnly ? 'True' : 'False'}</span>
                                    </div>
                                  </div>

                                  {mockProvisionResult.safeSummary && (
                                    <div className="mt-2 text-[10px] text-emerald-700 bg-emerald-100/30 p-2 rounded-lg font-mono overflow-auto max-h-32">
                                      {JSON.stringify(mockProvisionResult.safeSummary, null, 2)}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 shadow-sm space-y-2">
                                  <div className="flex items-center gap-2 text-rose-800">
                                    <Info className="w-5 h-5" />
                                    <span className="font-black text-sm">การจำลองล้มเหลว</span>
                                  </div>

                                  <div className="space-y-1 text-xs">
                                    <div className="flex gap-2">
                                      <span className="text-rose-700/70 w-20">Error Code:</span>
                                      <code className="font-mono font-bold text-rose-900 bg-rose-100/50 px-1.5 py-0.5 rounded">
                                        {mockProvisionResult.errorCode}
                                      </code>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-rose-700/70 w-20">Retry Safe:</span>
                                      <span className="font-semibold text-rose-800">{mockProvisionResult.safeToRetry ? 'Yes' : 'No'}</span>
                                    </div>
                                  </div>

                                  {(mockProvisionResult.warnings && mockProvisionResult.warnings.length > 0) && (
                                    <div className="mt-2 text-[10px] text-rose-700 bg-rose-100/30 p-2 rounded-lg list-disc list-inside">
                                      {mockProvisionResult.warnings.map((w: string, i: number) => <div key={i}>• {w}</div>)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                    </>
                  )}

                  {/* JSON Display */}
                  <div className="relative">
                    <div className="absolute top-0 right-0 p-3 flex items-center gap-2">
                      <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-800 px-2.5 py-1 rounded shadow-sm border border-slate-700 select-none">
                        JSON PAYLOAD
                      </span>
                    </div>
                    <pre className="bg-slate-900 text-slate-300 p-5 rounded-2xl overflow-x-auto text-[11px] sm:text-xs font-mono shadow-inner border border-slate-800 leading-relaxed max-h-[500px] overflow-y-auto w-full">
                      <code>{JSON.stringify(integrationPayload, null, 2)}</code>
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

      </div>

    </div>
  );
};
